import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type { RirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'
import type { BasicData } from '@/utils/format-edit/basicData'

type BizreachItem = {
  ig_cd?: string
  i_cd?: string
  jg_cd?: string
  j_cd?: string
  name: string
  category_name: string
} & Record<string, string>

type RequestAiAxiosVm = {
  $axios: {
    post: (
      url: string,
      data: unknown,
      config?: { timeout: number }
    ) => Promise<{
      data: {
        success?: boolean
        message?: string
        industries?: BizreachItem[]
        jobCategories?: BizreachItem[]
        yakushoku?: string
        industry_cd?: string
        job_category_cd?: string
        hope_industry_cd?: string
        hope_job_category_cd?: string
        now_nensyu?: string
      }
    }>
  }
}

type Options = {
  formatData: RirekishoFormatData
  basicData: () => BasicData
  emitUpdateBasicData: (payload: Partial<BasicData>) => void
  enabled?: boolean
}

type BizreachGroup = {
  label: string
  options: { value: string; label: string }[]
  ig_cd?: string
  jg_cd?: string
}

function groupByCategory(
  items: BizreachItem[],
  groupKey: 'ig_cd' | 'jg_cd',
  codeKey: 'i_cd' | 'j_cd'
): BizreachGroup[] {
  const groups: Record<string, BizreachGroup> = {}
  items.forEach((item) => {
    const gk = item[groupKey] || ''
    if (!groups[gk]) {
      groups[gk] = { label: item.category_name, options: [] }
      groups[gk][groupKey] = gk
    }
    groups[gk].options.push({
      value: item[codeKey] || '',
      label: item.name
    })
  })
  return Object.values(groups)
}

export function useRirekishoRequestAi(options: Options) {
  const { vm, varuna } = useHostServices()
  const host = vm as unknown as RequestAiAxiosVm
  const { user, isAuthenticated } = useAuth()
  const enabled = options.enabled !== false

  const requestAiGenerated = ref(false)
  const requestAiLoading = ref(false)
  const requestAiError = ref('')
  const lastAiInputSnapshot = ref<string | null>(null)
  const bizreachIndustries = ref<BizreachItem[]>([])
  const bizreachJobCategories = ref<BizreachItem[]>([])

  const bizreachIndustryGroups = computed(() =>
    groupByCategory(bizreachIndustries.value, 'ig_cd', 'i_cd')
  )
  const bizreachJobCategoryGroups = computed(() =>
    groupByCategory(bizreachJobCategories.value, 'jg_cd', 'j_cd')
  )

  const industryNameMap = computed(() => {
    const map: Record<string, string> = {}
    bizreachIndustries.value.forEach((i) => {
      if (i.i_cd) map[i.i_cd] = i.name
    })
    return map
  })

  const jobCategoryNameMap = computed(() => {
    const map: Record<string, string> = {}
    bizreachJobCategories.value.forEach((j) => {
      if (j.j_cd) map[j.j_cd] = j.name
    })
    return map
  })

  function getIndustryName(cd: unknown) {
    const key = String(cd ?? '')
    if (!key) return ''
    return industryNameMap.value[key] || key
  }

  function getJobCategoryName(cd: unknown) {
    const key = String(cd ?? '')
    if (!key) return ''
    return jobCategoryNameMap.value[key] || key
  }

  function buildAiInputSnapshot() {
    return JSON.stringify({
      jobHistories: options.formatData.jobHistories,
      licenseHistories: options.formatData.licenseHistories,
      reason: options.formatData.reason,
      ai_company_name: options.formatData.ai_company_name
    })
  }

  function fetchBizreachMasters() {
    return host.$axios
      .post(varuna.endPoints.bizreachMasters, {}, { timeout: 30000 })
      .then((response) => {
        if (response.data.success) {
          bizreachIndustries.value = response.data.industries || []
          bizreachJobCategories.value = response.data.jobCategories || []
        }
      })
      .catch(() => {})
  }

  function restoreRequestAiState() {
    if (!enabled) return
    const basic = options.basicData()
    if (
      basic.yakushokuBusho ||
      basic.gyoushuCd ||
      basic.shokushuCd ||
      basic.kibouGyoushuCd ||
      basic.kibouShokushuCd
    ) {
      requestAiGenerated.value = true
      lastAiInputSnapshot.value = buildAiInputSnapshot()
      if (bizreachIndustries.value.length === 0) {
        fetchBizreachMasters()
      }
    }
  }

  function getAIAutoRequest() {
    if (!enabled) return
    requestAiLoading.value = true
    requestAiError.value = ''
    const basic = options.basicData()
    const postData = {
      uuid: isAuthenticated.value && user.value?.uid ? user.value.uid : '',
      jobHistories: options.formatData.jobHistories,
      licenseHistories: options.formatData.licenseHistories,
      reason: options.formatData.reason,
      nowNensyu: options.formatData.nowNensyu,
      kibouNensyu: options.formatData.kibouNensyu,
      ai_company_name: options.formatData.ai_company_name || '',
      birthYear: basic.birthYear || ''
    }
    host.$axios
      .post(varuna.endPoints.aiAutoRequest, postData, { timeout: 60000 })
      .then((response) => {
        if (response.data.success) {
          const aiFields = {
            yakushokuBusho: response.data.yakushoku,
            gyoushuCd: response.data.industry_cd,
            shokushuCd: response.data.job_category_cd,
            kibouGyoushuCd: response.data.hope_industry_cd,
            kibouShokushuCd: response.data.hope_job_category_cd
          }
          // AIが決められなかった項目は空文字で本人の入力を消さないよう、キーごと落とす
          const payload: Partial<BasicData> = {}
          ;(Object.keys(aiFields) as (keyof typeof aiFields)[]).forEach(
            (key) => {
              const value = aiFields[key]
              if (value) payload[key] = value
            }
          )
          if (Object.keys(payload).length > 0) {
            options.emitUpdateBasicData(payload)
          }
          // 本人が選んだ年収を引き下げないよう、未入力のときだけ反映する
          if (response.data.now_nensyu && !options.formatData.nowNensyu) {
            options.formatData.nowNensyu = response.data.now_nensyu
          }
          requestAiGenerated.value = true
          lastAiInputSnapshot.value = buildAiInputSnapshot()
        } else {
          requestAiError.value =
            response.data.message || 'AI自動入力に失敗しました。'
        }
      })
      .catch((error: unknown) => {
        const err = error as { response?: { data?: { message?: string } } }
        requestAiError.value =
          (err.response && err.response.data && err.response.data.message) ||
          'AI自動入力でエラーが発生しました。'
      })
      .finally(() => {
        requestAiLoading.value = false
      })
  }

  function runAutoRequestIfNeeded() {
    if (!enabled) return
    const runAi = () => {
      const currentSnapshot = buildAiInputSnapshot()
      if (
        lastAiInputSnapshot.value === null ||
        lastAiInputSnapshot.value !== currentSnapshot
      ) {
        getAIAutoRequest()
      }
    }
    if (bizreachIndustries.value.length === 0) {
      fetchBizreachMasters().then(runAi)
    } else {
      runAi()
    }
  }

  onBeforeMount(() => {
    if (!enabled) return
    emitter.on('local-format-data-loaded', restoreRequestAiState)
  })

  onBeforeUnmount(() => {
    if (!enabled) return
    emitter.off('local-format-data-loaded', restoreRequestAiState)
  })

  return {
    requestAiGenerated,
    requestAiLoading,
    requestAiError,
    bizreachIndustryGroups,
    bizreachJobCategoryGroups,
    getIndustryName,
    getJobCategoryName,
    runAutoRequestIfNeeded
  }
}
